import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { LoopRange, MediaPlayer } from '../Timeline/useMediaPlayer';
import { debugLog } from '../../utils/debugLog';
import { planAudioSync, snapshotMedia } from '../../utils/multicamAudioSync';
import { normalizeFps, stepFrames as stepFrameTime } from '../../utils/timecode';
import {
  ReviewAngle,
  angleCovers,
  hasKnownLength,
  soundSourceAt,
  sourceEndMs,
  timelineLengthMs,
  withMeasuredDuration,
} from './multicamReview';
import { RECORDING_KEY } from './useSyncedAngles';

/**
 * The multicam studio's player. It drives the studio's TransportBar and
 * VideoStage through the same `MediaPlayer` interface as the single-video
 * studio, but every time it reports or takes is on the set's shared timeline
 * (the "program"), not the file on screen:
 *
 *   sourceSeconds = programSeconds - timelineStart + sourceIn
 *
 * One <video> shows the chosen angle. Choosing another angle swaps its source
 * and resumes at the same program time (and keeps playing if it was).
 * Seeking or playing into a stretch the angle on screen did not record
 * switches to an angle that did. The set's audio angle is heard: when another
 * angle is on screen the video is muted and a hidden <audio> follows it (see
 * planAudioSync). Frame steps, skips, loops and the J/K/L shuttle all work in
 * program time, so timecodes stay frame-accurate whichever angle is showing.
 * A set with a separate sound recording plays that in the hidden <audio>
 * instead, whatever is on screen (its key is RECORDING_KEY). Where the set's
 * sound has no footage (clips placed one after another, say), the angle on
 * screen is heard instead of silence.
 */

export interface MulticamPlayer<A extends ReviewAngle = ReviewAngle> extends MediaPlayer {
  /** The set's angles, with lengths measured from the media filled in. */
  angles: A[];
  viewKey: string;
  viewAngle: A | null;
  audioAngle: A | null;
  /** True when the sound comes from an angle that is not on screen. */
  separateAudio: boolean;
  /** Whether an angle has footage at `seconds` (default: the playhead). */
  hasFootage: (angleKey: string, seconds?: number) => boolean;
  /**
   * What is heard at `seconds` (default: the playhead): the set's sound where
   * it has footage, else the angle on screen where it has; null for silence.
   */
  heardKeyAt: (seconds?: number) => string | null;
  /** Show another angle at the playhead. False when it has no footage there. */
  selectAngle: (angleKey: string) => boolean;
  /** Callback ref for the hidden <audio> that plays the audio angle. */
  audioRef: (node: HTMLAudioElement | null) => void;
}

interface MulticamPlayerOptions<A extends ReviewAngle> {
  angles: A[];
  audioAngleKey?: string | null;
  /** The set's separate sound recording (timed like an angle): heard instead of any angle. */
  audioSource?: ReviewAngle | null;
  /** The angle shown first (the master). */
  initialAngleKey?: string;
  fps?: number;
  /** In/out loop, in program seconds. */
  loop?: LoopRange;
}

interface PendingSource {
  /** Program seconds to show once the angle's source is ready. */
  time: number;
  angleKey: string;
  /** Both angles use the same file, so no new load (and no metadata event) follows. */
  sameSource: boolean;
}

const UI_UPDATE_MS = 50;
const SHUTTLE_LEVELS = [1, 2, 4];
const REVERSE_STEP_MS = 50;

const toSourceSeconds = (angle: ReviewAngle, seconds: number) =>
  (seconds * 1000 - angle.timelineStartMs + (angle.sourceInMs || 0)) / 1000;

const toProgramSeconds = (angle: ReviewAngle, sourceSeconds: number) =>
  (sourceSeconds * 1000 - (angle.sourceInMs || 0) + angle.timelineStartMs) / 1000;

/** A source position inside the angle's usable footage. */
const clampToFootage = (angle: ReviewAngle, sourceSeconds: number) => {
  const end = sourceEndMs(angle);
  const min = (angle.sourceInMs || 0) / 1000;
  const max = end === null ? Number.POSITIVE_INFINITY : end / 1000;
  return Math.min(max, Math.max(min, sourceSeconds));
};

const covers = (angle: ReviewAngle | null | undefined, seconds: number) =>
  Boolean(angle) && angleCovers(angle as ReviewAngle, seconds * 1000);

const describeMediaError = (el: HTMLMediaElement): string => {
  const code = el.error?.code;
  if (code === 2) return 'A network error stopped this angle from loading.';
  if (code === 3) return 'This angle’s file could not be decoded.';
  if (code === 4) return 'This angle’s file or link is not supported by your browser.';
  return 'This angle could not be loaded.';
};

const nextFrame = (callback: FrameRequestCallback): number =>
  typeof window.requestAnimationFrame === 'function'
    ? window.requestAnimationFrame(callback)
    : window.setTimeout(() => callback(performance.now()), 16);

const cancelFrame = (handle: number) => {
  if (typeof window.cancelAnimationFrame === 'function') window.cancelAnimationFrame(handle);
  else window.clearTimeout(handle);
};

export function useMulticamPlayer<A extends ReviewAngle>({
  angles,
  audioAngleKey,
  audioSource,
  initialAngleKey,
  fps,
  loop,
}: MulticamPlayerOptions<A>): MulticamPlayer<A> {
  const rateFps = normalizeFps(fps);

  // Real lengths from the media, for angles saved before theirs was recorded.
  const [measured, setMeasured] = useState<Record<string, number>>({});
  const effective = useMemo(
    () => angles.map((angle) => withMeasuredDuration(angle, measured[angle.angleKey]) as A),
    [angles, measured]
  );
  const duration = useMemo(() => timelineLengthMs(effective) / 1000, [effective]);

  const fallbackKey = useMemo(() => {
    if (initialAngleKey && effective.some((angle) => angle.angleKey === initialAngleKey)) return initialAngleKey;
    return (effective.find((angle) => angle.angleKey === 'A') ?? effective[0])?.angleKey ?? '';
  }, [effective, initialAngleKey]);
  const [chosenKey, setChosenKey] = useState(fallbackKey);
  const viewAngle = effective.find((angle) => angle.angleKey === chosenKey)
    ?? effective.find((angle) => angle.angleKey === fallbackKey)
    ?? null;
  const viewKey = viewAngle?.angleKey ?? '';
  // The recording never sets the program's length: the cameras do.
  const recording = useMemo(
    () => (audioSource?.videoUrl
      ? (withMeasuredDuration({ ...audioSource, angleKey: RECORDING_KEY }, measured[RECORDING_KEY]) as A)
      : null),
    [audioSource, measured]
  );
  const audioAngle = recording
    ?? effective.find((angle) => angle.angleKey === audioAngleKey)
    ?? effective.find((angle) => angle.angleKey === 'A')
    ?? effective[0]
    ?? null;
  const separateAudio = Boolean(audioAngle && viewAngle && audioAngle.angleKey !== viewAngle.angleKey);

  const [element, setElement] = useState<HTMLVideoElement | null>(null);
  const [audioEl, setAudioEl] = useState<HTMLAudioElement | null>(null);
  const [currentTime, setCurrentTimeState] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [buffering, setBuffering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [volume, setVolumeState] = useState(1);
  const [muted, setMuted] = useState(false);
  const [rate, setRateState] = useState(1);
  const [shuttle, setShuttle] = useState(0);

  const elRef = useRef<HTMLVideoElement | null>(null);
  const audioElRef = useRef<HTMLAudioElement | null>(null);
  /** Program seconds; exact, unlike the throttled state. */
  const timeRef = useRef(0);
  const pendingRef = useRef<PendingSource | null>(null);
  /** Keep playing once the next angle's source is ready. */
  const resumeRef = useRef(false);
  /** No angle has footage at the playhead, so the element is parked and its time is not the program's. */
  const parkedRef = useRef(false);
  const shuttleRef = useRef(0);
  const reverseTimerRef = useRef<number | null>(null);
  const lastAudioFixRef = useRef(0);
  /** The video on screen is heard because the set's sound has no footage here. */
  const borrowedRef = useRef(false);

  // Latest values for listeners that are bound once per element.
  const live = useRef({ angles, effective, viewAngle, audioAngle, audioSource, separateAudio, duration, rate, muted, volume, loop, rateFps });
  live.current = { angles, effective, viewAngle, audioAngle, audioSource, separateAudio, duration, rate, muted, volume, loop, rateFps };

  const ref = useCallback((node: HTMLVideoElement | null) => {
    elRef.current = node;
    setElement(node);
  }, []);
  const audioRef = useCallback((node: HTMLAudioElement | null) => {
    audioElRef.current = node;
    setAudioEl(node);
  }, []);

  const setTime = useCallback((seconds: number) => {
    timeRef.current = seconds;
    setCurrentTimeState(seconds);
  }, []);

  const applyShuttle = useCallback((level: number) => {
    shuttleRef.current = level;
    setShuttle(level);
  }, []);

  const stopReverse = useCallback(() => {
    if (reverseTimerRef.current !== null) {
      window.clearInterval(reverseTimerRef.current);
      reverseTimerRef.current = null;
    }
  }, []);

  const startPlayback = useCallback((el: HTMLMediaElement) => {
    try {
      const result = el.play() as Promise<void> | undefined;
      if (result && typeof result.catch === 'function') {
        result.catch((err: unknown) => {
          // A pause or a source change interrupted play(); that is expected.
          if ((err as { name?: string })?.name !== 'AbortError') debugLog('Multicam play() was rejected:', err);
        });
      }
    } catch (err) {
      debugLog('Multicam play() threw:', err);
    }
  }, []);

  /** Exact program time: from the element when it is showing the program. */
  const getTime = useCallback(() => {
    const el = elRef.current;
    const view = live.current.viewAngle;
    if (!el || !view || pendingRef.current || parkedRef.current) return timeRef.current;
    return Math.max(0, toProgramSeconds(view, el.currentTime));
  }, []);

  const isPlayingIntent = useCallback(() => {
    const el = elRef.current;
    return resumeRef.current || reverseTimerRef.current !== null || Boolean(el && !el.paused);
  }, []);

  /**
   * Whether the video on screen should be heard in place of the set's sound:
   * the sound has no footage at the playhead and the angle on screen has.
   * False while a switch is pending or the picture is parked.
   */
  const shouldBorrow = useCallback(() => {
    const video = elRef.current;
    const { viewAngle: view, audioAngle: sound, separateAudio: separate } = live.current;
    if (!separate || !video || !view || !sound || video.readyState < 1 || pendingRef.current || parkedRef.current) return false;
    const programSeconds = toProgramSeconds(view, video.currentTime);
    return !covers(sound, programSeconds) && covers(view, programSeconds);
  }, []);

  /** Exactly one element is audible: unmutes the video while it is borrowed. */
  const applyVideoSound = useCallback((borrowed: boolean) => {
    borrowedRef.current = borrowed;
    const video = elRef.current;
    if (!video) return;
    const wanted = live.current.muted || (live.current.separateAudio && !borrowed);
    if (video.muted !== wanted) video.muted = wanted;
  }, []);

  /**
   * Keep the hidden audio element in step with the visible video, which is
   * the clock. `force` re-aligns now (a seek finished, a source loaded);
   * otherwise only real drift is corrected (see planAudioSync).
   */
  const syncAudio = useCallback((force: boolean) => {
    applyVideoSound(shouldBorrow());
    const audio = audioElRef.current;
    if (!audio) return;
    const video = elRef.current;
    const { viewAngle: view, audioAngle: sound, separateAudio: separate } = live.current;
    // Right after an angle switch the video reports currentTime 0, which
    // would send the audio to the wrong place.
    if (!separate || !video || !view || !sound || video.readyState < 1 || pendingRef.current || parkedRef.current) {
      if (!audio.paused) audio.pause();
      return;
    }
    const programSeconds = toProgramSeconds(view, video.currentTime);
    const targetS = covers(sound, programSeconds) ? toSourceSeconds(sound, programSeconds) : null;
    const nowMs = Date.now();
    const plan = planAudioSync({
      video: snapshotMedia(video),
      audio: snapshotMedia(audio),
      targetS,
      force,
      nowMs,
      lastCorrectionAtMs: lastAudioFixRef.current,
    });
    if (plan.seekToS !== null) {
      try {
        audio.currentTime = plan.seekToS;
        lastAudioFixRef.current = nowMs;
      } catch {
        // Not seekable yet; its loadedmetadata event re-syncs.
      }
    }
    if (plan.pause) audio.pause();
    if (plan.play) startPlayback(audio);
  }, [applyVideoSound, shouldBorrow, startPlayback]);

  const bestAngleAt = useCallback((seconds: number, preferKey?: string): A | null => {
    const all = live.current.effective;
    const preferred = all.find((angle) => angle.angleKey === preferKey);
    if (preferred && covers(preferred, seconds)) return preferred;
    return all.find((angle) => covers(angle, seconds)) ?? null;
  }, []);

  /** Show `angleKey` at program time `seconds`; `resume` keeps playing. */
  const switchView = useCallback((angleKey: string, seconds: number, resume: boolean) => {
    const { viewAngle: current, effective: all } = live.current;
    const next = all.find((angle) => angle.angleKey === angleKey);
    if (!next) return;
    parkedRef.current = false;
    setTime(seconds);
    pendingRef.current = {
      time: seconds,
      angleKey,
      sameSource: Boolean(current && elRef.current && current.videoUrl === next.videoUrl),
    };
    resumeRef.current = resume;
    if (resume) {
      setBuffering(true);
      setPlaying(true);
    }
    setChosenKey(angleKey);
  }, [setTime]);

  /** Show the program time a switch was waiting for, once the source is ready. */
  const applyPending = useCallback(() => {
    const el = elRef.current;
    const pending = pendingRef.current;
    const view = live.current.viewAngle;
    if (!el || !pending || !view || pending.angleKey !== view.angleKey) return;
    pendingRef.current = null;
    el.playbackRate = shuttleRef.current > 1 ? shuttleRef.current : live.current.rate;
    if (!covers(view, pending.time)) {
      // The playhead moved while this angle loaded, to where it has no footage.
      const best = bestAngleAt(pending.time, view.angleKey);
      if (best && best.angleKey !== view.angleKey) {
        switchView(best.angleKey, pending.time, resumeRef.current);
        return;
      }
      parkedRef.current = true;
      resumeRef.current = false;
      setPlaying(false);
      setBuffering(false);
      setTime(pending.time);
      return;
    }
    try {
      el.currentTime = clampToFootage(view, toSourceSeconds(view, pending.time));
    } catch {
      // Not seekable yet; loadedmetadata re-applies the playhead.
    }
    setTime(pending.time);
    if (resumeRef.current) {
      resumeRef.current = false;
      startPlayback(el);
    } else {
      setBuffering(false);
    }
    syncAudio(true);
  }, [bestAngleAt, setTime, startPlayback, switchView, syncAudio]);

  const seek = useCallback((seconds: number) => {
    const { duration: total, viewAngle: view } = live.current;
    const wanted = Math.max(0, Number.isFinite(seconds) ? seconds : 0);
    const t = total > 0 ? Math.min(total, wanted) : wanted;
    const el = elRef.current;
    setTime(t);
    if (!el || !view) return;
    if (pendingRef.current) {
      // Another angle is still loading: it opens at the new time.
      pendingRef.current = { ...pendingRef.current, time: t };
      return;
    }
    if (covers(view, t)) {
      parkedRef.current = false;
      try {
        el.currentTime = clampToFootage(view, toSourceSeconds(view, t));
      } catch (err) {
        debugLog('Seek failed before metadata loaded:', err);
      }
      return;
    }
    // The angle on screen did not record this moment: show one that did.
    const best = bestAngleAt(t, view.angleKey);
    if (best) {
      switchView(best.angleKey, t, isPlayingIntent());
      return;
    }
    // No angle has footage here; hold the picture.
    parkedRef.current = true;
    resumeRef.current = false;
    if (!el.paused) el.pause();
    setPlaying(false);
  }, [bestAngleAt, isPlayingIntent, setTime, switchView]);

  /** The angle on screen ran out of footage (its out point, or the file ended). */
  const handleRunOut = useCallback(() => {
    const el = elRef.current;
    const { viewAngle: view, duration: total } = live.current;
    if (!el || !view || pendingRef.current) return;
    const t = Math.max(0, toProgramSeconds(view, el.currentTime));
    if (total > 0 && t >= total - 1e-3) {
      el.pause();
      setPlaying(false);
      setTime(total);
      return;
    }
    const next = live.current.effective.find((angle) => angle.angleKey !== view.angleKey && covers(angle, t));
    if (next) {
      switchView(next.angleKey, t, true);
      return;
    }
    el.pause();
    setPlaying(false);
    setTime(t);
  }, [setTime, switchView]);

  const playFrom = useCallback((keepShuttle: boolean) => {
    stopReverse();
    if (!keepShuttle) applyShuttle(0);
    const el = elRef.current;
    const { duration: total, loop: range, viewAngle: view } = live.current;
    if (!el || !view) return;
    let t = getTime();
    if (total > 0 && t >= total - 1e-3) {
      t = 0;
      seek(0);
    }
    if (
      range?.enabled && typeof range.start === 'number' && typeof range.end === 'number' &&
      range.end > range.start && (t >= range.end || t < range.start)
    ) {
      t = range.start;
      seek(range.start);
    }
    if (pendingRef.current) {
      resumeRef.current = true;
      setBuffering(true);
      setPlaying(true);
      return;
    }
    if (!covers(live.current.viewAngle, t)) {
      const best = bestAngleAt(t, view.angleKey);
      if (best) switchView(best.angleKey, t, true);
      return;
    }
    if (!keepShuttle) el.playbackRate = live.current.rate;
    startPlayback(el);
  }, [applyShuttle, bestAngleAt, getTime, seek, startPlayback, stopReverse, switchView]);

  const play = useCallback(() => playFrom(false), [playFrom]);

  const pause = useCallback(() => {
    stopReverse();
    applyShuttle(0);
    resumeRef.current = false;
    const el = elRef.current;
    if (!el) return;
    el.pause();
    el.playbackRate = live.current.rate;
    setPlaying(false);
    setBuffering(false);
    syncAudio(false);
  }, [applyShuttle, stopReverse, syncAudio]);

  const toggle = useCallback(() => {
    if (!elRef.current) return;
    if (isPlayingIntent()) pause();
    else play();
  }, [isPlayingIntent, pause, play]);

  const skip = useCallback((deltaSeconds: number) => seek(getTime() + deltaSeconds), [getTime, seek]);

  const stepFrames = useCallback((frames: number) => {
    const el = elRef.current;
    if (!el) return;
    stopReverse();
    applyShuttle(0);
    resumeRef.current = false;
    if (!el.paused) el.pause();
    setPlaying(false);
    const total = live.current.duration;
    seek(stepFrameTime(getTime(), frames, live.current.rateFps, total > 0 ? total : undefined));
  }, [applyShuttle, getTime, seek, stopReverse]);

  const setVolume = useCallback((next: number) => {
    const value = Math.min(1, Math.max(0, next));
    setVolumeState(value);
    if (value > 0) setMuted(false);
  }, []);

  const toggleMute = useCallback(() => setMuted((value) => !value), []);

  const setRate = useCallback((next: number) => {
    setRateState(next);
    const el = elRef.current;
    if (el && shuttleRef.current <= 0) el.playbackRate = next;
  }, []);

  const shuttleForward = useCallback(() => {
    const el = elRef.current;
    if (!el) return;
    stopReverse();
    const current = shuttleRef.current;
    const level = current <= 0
      ? 1
      : SHUTTLE_LEVELS[Math.min(SHUTTLE_LEVELS.length - 1, SHUTTLE_LEVELS.indexOf(current) + 1)];
    applyShuttle(level);
    el.playbackRate = level === 1 ? live.current.rate : level;
    if (el.paused && !resumeRef.current) playFrom(true);
  }, [applyShuttle, playFrom, stopReverse]);

  const shuttleReverse = useCallback(() => {
    const el = elRef.current;
    if (!el) return;
    resumeRef.current = false;
    if (!el.paused) el.pause();
    setPlaying(false);
    const current = shuttleRef.current;
    const level = current >= 0
      ? -1
      : -SHUTTLE_LEVELS[Math.min(SHUTTLE_LEVELS.length - 1, SHUTTLE_LEVELS.indexOf(-current) + 1)];
    stopReverse();
    reverseTimerRef.current = window.setInterval(() => {
      const next = getTime() - (REVERSE_STEP_MS / 1000) * Math.abs(level);
      if (next <= 0) {
        seek(0);
        stopReverse();
        applyShuttle(0);
        return;
      }
      seek(next);
    }, REVERSE_STEP_MS);
    applyShuttle(level);
  }, [applyShuttle, getTime, seek, stopReverse]);

  const selectAngle = useCallback((angleKey: string) => {
    const { viewAngle: view, effective: all } = live.current;
    if (view?.angleKey === angleKey) return true;
    const target = all.find((angle) => angle.angleKey === angleKey);
    if (!target) return false;
    const t = getTime();
    if (!covers(target, t)) return false;
    switchView(angleKey, t, isPlayingIntent());
    return true;
  }, [getTime, isPlayingIntent, switchView]);

  /** Fill in an angle's length from its media when the set did not record it. */
  const measure = useCallback((angleKey: string | undefined, el: HTMLMediaElement) => {
    if (!angleKey) return;
    const original = angleKey === RECORDING_KEY
      ? live.current.audioSource
      : live.current.angles.find((angle) => angle.angleKey === angleKey);
    if (!original || hasKnownLength(original)) return;
    const seconds = el.duration;
    if (!Number.isFinite(seconds) || seconds <= 0) return;
    setMeasured((previous) => (previous[angleKey] ? previous : { ...previous, [angleKey]: Math.round(seconds * 1000) }));
  }, []);

  // Video element events.
  useEffect(() => {
    const el = element;
    if (!el) return undefined;

    const onLoadedMetadata = () => {
      measure(live.current.viewAngle?.angleKey, el);
      el.muted = live.current.muted || live.current.separateAudio;
      el.volume = live.current.volume;
      if (pendingRef.current) {
        applyPending();
        return;
      }
      // First load (or a retry): open at the playhead, not the file's start.
      const view = live.current.viewAngle;
      el.playbackRate = shuttleRef.current > 1 ? shuttleRef.current : live.current.rate;
      if (view && !parkedRef.current) {
        if (covers(view, timeRef.current)) {
          try {
            el.currentTime = clampToFootage(view, toSourceSeconds(view, timeRef.current));
          } catch {
            // Not seekable yet.
          }
        } else {
          // This angle starts later than the playhead: show one that has it.
          seek(timeRef.current);
        }
      }
      syncAudio(true);
    };
    const readTime = () => {
      if (pendingRef.current || parkedRef.current) return;
      const view = live.current.viewAngle;
      if (!view) return;
      setTime(Math.max(0, toProgramSeconds(view, el.currentTime)));
    };
    const onTimeUpdate = () => {
      readTime();
      syncAudio(false);
    };
    const onSeeked = () => {
      readTime();
      syncAudio(true);
    };
    const onPlay = () => {
      setPlaying(true);
      setError(null);
      syncAudio(false);
    };
    const onPlaying = () => {
      setPlaying(true);
      setBuffering(false);
      syncAudio(false);
    };
    const onPause = () => {
      // Switching angles mid-playback, or the end of a file the program goes past.
      if ((pendingRef.current && resumeRef.current) || el.ended) return;
      setPlaying(false);
      readTime();
      syncAudio(false);
    };
    const onEnded = () => handleRunOut();
    const onWaiting = () => {
      setBuffering(true);
      syncAudio(false);
    };
    const onCanPlay = () => {
      setBuffering(false);
      syncAudio(false);
    };
    const onError = () => {
      setError(describeMediaError(el));
      setBuffering(false);
      setPlaying(false);
      resumeRef.current = false;
    };
    const onLoadStart = () => setError(null);
    const onDurationChange = () => measure(live.current.viewAngle?.angleKey, el);

    el.addEventListener('loadedmetadata', onLoadedMetadata);
    el.addEventListener('durationchange', onDurationChange);
    el.addEventListener('timeupdate', onTimeUpdate);
    el.addEventListener('seeked', onSeeked);
    el.addEventListener('play', onPlay);
    el.addEventListener('playing', onPlaying);
    el.addEventListener('pause', onPause);
    el.addEventListener('ended', onEnded);
    el.addEventListener('waiting', onWaiting);
    el.addEventListener('canplay', onCanPlay);
    el.addEventListener('error', onError);
    el.addEventListener('loadstart', onLoadStart);
    // A cached file can have its metadata before these listeners exist.
    if (el.readyState >= 1) onLoadedMetadata();
    return () => {
      el.removeEventListener('loadedmetadata', onLoadedMetadata);
      el.removeEventListener('durationchange', onDurationChange);
      el.removeEventListener('timeupdate', onTimeUpdate);
      el.removeEventListener('seeked', onSeeked);
      el.removeEventListener('play', onPlay);
      el.removeEventListener('playing', onPlaying);
      el.removeEventListener('pause', onPause);
      el.removeEventListener('ended', onEnded);
      el.removeEventListener('waiting', onWaiting);
      el.removeEventListener('canplay', onCanPlay);
      el.removeEventListener('error', onError);
      el.removeEventListener('loadstart', onLoadStart);
    };
  }, [element, applyPending, handleRunOut, measure, seek, setTime, syncAudio]);

  // Audio element events.
  useEffect(() => {
    const audio = audioEl;
    if (!audio) return undefined;
    const onLoadedMetadata = () => {
      // Loading a new source resets these on the element.
      audio.playbackRate = live.current.rate;
      audio.muted = live.current.muted;
      measure(live.current.audioAngle?.angleKey, audio);
      syncAudio(true);
    };
    const onCanPlay = () => syncAudio(false);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('canplay', onCanPlay);
    return () => {
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('canplay', onCanPlay);
    };
  }, [audioEl, measure, syncAudio]);

  // Two angles on one file: the switch needs no load, so apply it now.
  useEffect(() => {
    const pending = pendingRef.current;
    if (pending?.sameSource && pending.angleKey === viewKey) applyPending();
  }, [viewKey, applyPending]);

  // Exactly one element is audible: the video when it shows the audio angle,
  // otherwise the hidden audio element. Set directly; React does not
  // reliably apply the `muted` prop.
  useEffect(() => {
    if (element) {
      element.muted = muted || separateAudio;
      element.volume = volume;
    }
    if (audioEl) {
      audioEl.muted = muted;
      audioEl.volume = volume;
    }
    syncAudio(true);
  }, [element, audioEl, muted, volume, separateAudio, syncAudio]);

  useEffect(() => {
    if (element && shuttle <= 0) element.playbackRate = rate;
    if (audioEl) audioEl.playbackRate = shuttle > 1 ? shuttle : rate;
  }, [element, audioEl, rate, shuttle]);

  // A smooth playhead while playing (timeupdate fires about 4 times a
  // second), plus the loop, the end of the program and angles running out.
  useEffect(() => {
    const el = element;
    if (!el || !playing) return undefined;
    let last = 0;
    let handle: number | null = null;
    const tick = (now: number) => {
      const { viewAngle: view, duration: total, loop: range } = live.current;
      if (view && !pendingRef.current && !parkedRef.current && !el.paused) {
        const t = Math.max(0, toProgramSeconds(view, el.currentTime));
        if (
          range?.enabled && typeof range.start === 'number' && typeof range.end === 'number' &&
          range.end > range.start && t >= range.end
        ) {
          seek(range.start);
        } else if (!covers(view, t) && t * 1000 > view.timelineStartMs) {
          handleRunOut();
        } else if (total > 0 && t >= total) {
          handleRunOut();
        } else {
          // Crossing the edge of the set's sound: switch what is heard on
          // this frame rather than at the next timeupdate.
          if (shouldBorrow() !== borrowedRef.current) syncAudio(true);
          if (now - last >= UI_UPDATE_MS) {
            last = now;
            setTime(t);
          }
        }
      }
      handle = nextFrame(tick);
    };
    handle = nextFrame(tick);
    return () => {
      if (handle !== null) cancelFrame(handle);
    };
  }, [element, playing, handleRunOut, seek, setTime, shouldBorrow, syncAudio]);

  useEffect(() => () => {
    stopReverse();
    elRef.current?.pause();
    audioElRef.current?.pause();
  }, [stopReverse]);

  const hasFootage = useCallback((angleKey: string, seconds?: number) => {
    const angle = angleKey === RECORDING_KEY
      ? live.current.audioAngle
      : live.current.effective.find((candidate) => candidate.angleKey === angleKey);
    return covers(angle, typeof seconds === 'number' ? seconds : timeRef.current);
  }, []);

  const heardKeyAt = useCallback((seconds?: number) => {
    const { audioAngle: sound, viewAngle: view } = live.current;
    const t = typeof seconds === 'number' ? seconds : timeRef.current;
    return soundSourceAt(sound, view ? [view] : [], t * 1000, view?.angleKey)?.angleKey ?? null;
  }, []);

  return {
    ref,
    element,
    currentTime,
    duration,
    playing,
    buffering,
    error,
    volume,
    muted,
    rate,
    shuttle,
    play,
    pause,
    toggle,
    seek,
    skip,
    stepFrames,
    setVolume,
    toggleMute,
    setRate,
    shuttleForward,
    shuttleReverse,
    getTime,
    angles: effective,
    viewKey,
    viewAngle,
    audioAngle,
    separateAudio,
    hasFootage,
    heardKeyAt,
    selectAngle,
    audioRef,
  };
}
